import { Camera, Edit2, Trash2 } from 'lucide-react';
import { colors } from '../../styles/colors';

// --- Helpers ---

function getRoleBadgeStyle(role) {
  const base = {
    padding: '0.25rem 0.75rem',
    borderRadius: '1rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
  };
  switch (role) {
    case 'super_admin': return { ...base, backgroundColor: colors.background.lightRed,  color: colors.text.darkRed };
    case 'consultant':  return { ...base, backgroundColor: colors.background.paleBlue,  color: colors.text.darkBlue };
    case 'visiteur':    return { ...base, backgroundColor: colors.background.lightGray,  color: colors.text.secondary };
    default:            return base;
  }
}

function getRoleLabel(role) {
  switch (role) {
    case 'super_admin': return 'Super Admin';
    case 'consultant':  return 'Consultant';
    case 'visiteur':    return 'Visiteur';
    default:            return role;
  }
}

function getConsultantName(consultantId, consultants) {
  if (!consultantId) return '-';
  const c = consultants.find(c => c.id === consultantId);
  return c?.fields?.Nom || '-';
}

// --- Styles boutons d'action ---

const actionBtnBase = {
  padding: '0.5rem',
  color: colors.text.white,
  border: 'none',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 200ms ease-in-out',
};

const thStyle = {
  padding: '1rem',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: colors.text.white,
};

// --- Composant ---

function UsersTable({ filteredUsers, consultants, onEdit, onDelete, onPhoto, onForcePassword }) {
  return (
    <div style={{
      backgroundColor: colors.text.white,
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      overflow: 'hidden',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '"Noto Sans", sans-serif' }}>
        <thead>
          <tr style={{ backgroundColor: colors.primary.gray }}>
            <th style={{ ...thStyle, textAlign: 'left' }}>Email</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Nom</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Prénom</th>
            <th style={{ ...thStyle, textAlign: 'left' }}>Consultant</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Rôle</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Changer MDP</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user, index) => (
            <tr
              key={user.id}
              style={{
                backgroundColor: index % 2 === 0 ? colors.text.white : colors.secondary.lightGray,
                borderBottom: `1px solid ${colors.secondary.lightGray}`,
              }}
            >
              <td style={{ padding: '1rem', color: colors.primary.gray }}>{user.email}</td>
              <td style={{ padding: '1rem', color: colors.primary.gray }}>{user.nom}</td>
              <td style={{ padding: '1rem', color: colors.primary.gray }}>{user.prenom}</td>
              <td style={{ padding: '1rem', color: colors.secondary.mediumGray, fontStyle: user.consultant_id ? 'normal' : 'italic' }}>
                {getConsultantName(user.consultant_id, consultants)}
              </td>
              <td style={{ padding: '1rem', textAlign: 'center' }}>
                <span style={getRoleBadgeStyle(user.role)}>{getRoleLabel(user.role)}</span>
              </td>
              <td style={{ padding: '1rem', textAlign: 'center' }}>
                <span style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: user.actif ? colors.background.paleGreen : colors.background.lightRed,
                  color: user.actif ? colors.text.darkGreen2 : colors.text.darkRed,
                }}>
                  {user.actif ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td style={{ padding: '1rem', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={user.force_password_change || false}
                  onChange={() => onForcePassword(user.id, user.force_password_change)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: colors.primary.teal }}
                  title={user.force_password_change ? 'Doit changer son mot de passe' : 'Cliquer pour forcer le changement de mot de passe'}
                />
              </td>
              <td style={{ padding: '1rem', textAlign: 'center' }}>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button
                    onClick={() => onPhoto(user)}
                    style={{ ...actionBtnBase, backgroundColor: colors.primary.teal }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f6a'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary.teal}
                    title="Modifier la photo"
                  >
                    <Camera size={16} />
                  </button>
                  <button
                    onClick={() => onEdit(user)}
                    style={{ ...actionBtnBase, backgroundColor: colors.secondary.lightBlue }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.text.darkBlue}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.secondary.lightBlue}
                    title="Modifier"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => onDelete(user)}
                    style={{ ...actionBtnBase, backgroundColor: colors.status.error }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover.errorDark}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.status.error}
                    title="Supprimer"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredUsers.length === 0 && (
        <div style={{ padding: '3rem', textAlign: 'center', color: colors.secondary.mediumGray, fontFamily: '"Noto Sans", sans-serif' }}>
          Aucun utilisateur trouvé
        </div>
      )}
    </div>
  );
}

export default UsersTable;
